import React from 'react'
import { useAtomValue } from 'jotai'
import styled from 'styled-components'
import dt from 'dayjs'
import * as state from '../../states'
import {
	ERoleAccess,
	TRevenueSpendByAccount,
	ERevenueAgencyFakeType,
	TRevenueSpendByAccountRevenueSpend,
	TRevenueSpendByAccountRevenueCpa,
	TRevenueSpendByAccountRevenue,
	TRevenueSpendTotalKeys,
	REVENUE_SPEND_TOTAL_KEYS,
	TRevenueSpendByTeam,
	TRevenueSpendByAccountSpend,
	TRevenueSpendAgg,
	TRevenueSpendByAccountOfferSpend,
	IRevenueAgencyDropDown
} from '../../types'
import {
	UiTable,
	IUiTableConfig,
	headCellFormatterSortable,
	ESortOrder,
	IconExternalLink,
	UiTotalLabel
} from '../ui'
import { REVENUE_SPEND_BY_ACCOUNT_PAGE_SORT_FIELDS, IRevenueSpendByAccountSortField } from '../../hooks'
import { Access } from '../../lib'
import * as helpers from '../../helpers'
import { renderTotal } from '../offerSpend'
import { useTranslation } from 'react-i18next'
import { ALL_LANG_KEY } from '../../types/lang'


type IRevenueSpendByAccountListTableProps = {
	items: TRevenueSpendByAccount[];
	skip: number;
	sortBy: IRevenueSpendByAccountSortField;
	setSortBy: (sortBy: IRevenueSpendByAccountSortField) => void;
	order: ESortOrder;
	setOrder: (order: ESortOrder) => void;
	onViewRevenueCpaDetails?: (item: TRevenueSpendByAccount | TRevenueSpendByAccountRevenueCpa, e: React.MouseEvent) => void;
	onViewRevenueSpendDetails?: (item: TRevenueSpendByAccount | TRevenueSpendByAccountRevenueSpend, e: React.MouseEvent) => void;
	onViewOfferSpendDetails?: (item: TRevenueSpendByAccount | TRevenueSpendByAccountOfferSpend, e: React.MouseEvent) => void;
	onViewRevenueDetails?: (item: TRevenueSpendByAccount | TRevenueSpendByAccountRevenue, e: React.MouseEvent) => void;
	onViewSpendDetails?: (item: TRevenueSpendByAccount | TRevenueSpendByAccountSpend, e: React.MouseEvent) => void;
	showEmptySpendAgencyColumns?: boolean;
}

const NO_DATA_CELL: React.ReactNode = '-'

const AmountBox = styled.div`
    min-width: 5ch;
    text-align: center;
`

const Amount = styled.span`
    position: relative;
    transition: all 0.4s;

    & .icon-link {
        position: absolute;
        left: 100%;
        top: 1px;
        opacity: 0;
        transition: all 0.4s;
    }

    &:hover {
        color: currentColor;

        .icon-link {
            color: currentColor;
            opacity: 1;
            transform: translateX(3px);
        }
    }
`

export function renderByRevenue<TFull, TRevenueOnly>(
	access: boolean | null,
	item: TFull | TRevenueOnly,
	onViewRevenueDetails?: (item: TFull | TRevenueOnly, e: React.MouseEvent) => void
) {
	return function (
		total: number,
		rowIndex: number,
		cellIndex: number,
		className?: string
	) {
		const id = `revenue-${rowIndex}:${cellIndex}`
		const formatValue = helpers.formatAmount(total)
		const formatValueWithSeparator = helpers.thousandSeparator(formatValue)

		if (total > 0) {
			return <>
				{
					!!onViewRevenueDetails && !!access
						? (
							<AmountBox>
								<Amount className={className ?? ''}
								        style={{
									        cursor: 'pointer'
								        }}
								        onClick={(e) => {
									        //console.log('RevenueSpendByAccountListTable: ', {item})
									        onViewRevenueDetails && onViewRevenueDetails(item, e)
								        }}
								>
									{formatValueWithSeparator}
									<i className={'icon-link'}>
										<IconExternalLink fill={'currentColor'}/>
									</i>
								</Amount>
							</AmountBox>
						)
						: null
				}
			</>
		}
		return <AmountBox><Amount className={className ?? ''}>{formatValueWithSeparator}</Amount></AmountBox>
	}
}

export function renderBySpend<TFull, TSpendOnly>(
	access: boolean | null,
	item: TFull | TSpendOnly,
	onViewSpendDetails?: (item: TFull | TSpendOnly, e: React.MouseEvent) => void
) {
	return function (
		total: number,
		rowIndex: number,
		cellIndex: number,
		className?: string
	) {
		const id = `spend-${rowIndex}:${cellIndex}`
		const formatValue = helpers.formatAmount(total)
		const formatValueWithSeparator = helpers.thousandSeparator(formatValue)

		if (total > 0) {
			return <>
				{
					!!onViewSpendDetails && !!access
						? (
							<AmountBox>
								<Amount className={className ?? ''}
								        style={{
									        cursor: 'pointer'
								        }}
								        onClick={(e) => {
									        //console.log('RevenueSpendByAccountListTable: renderBySpend:: ', { item })
									        onViewSpendDetails && onViewSpendDetails(item, e)
								        }}
								>
									{formatValueWithSeparator}
									<i className={'icon-link'}>
										<IconExternalLink fill={'currentColor'}/>
									</i>
								</Amount>
							</AmountBox>
						)
						: null
				}
			</>
		}
		return <AmountBox><Amount className={className ?? ''}>{formatValueWithSeparator}</Amount></AmountBox>
	}
}

export const getRevenueSpendTotalItemsAgg = (
	items: TRevenueSpendByAccount[] | TRevenueSpendByTeam[]
): Record<TRevenueSpendTotalKeys, number> => {
	const totalItemsAgg: Record<TRevenueSpendTotalKeys, number> = {
		spendTotal: 0,
		accountFBTotal: 0,
		creoTotal: 0,
		installsTotal: 0,
		agencyPercentTotal: 0,
		slicesTotal: 0,
		profit: 0,
		roi: 0,
		revenueTotal: 0,
		revenueCpaTotal: 0,
		revenueSpendTotal: 0,
		offerSpendTotal: 0
	}

	items.forEach(item => {
		for (const key of REVENUE_SPEND_TOTAL_KEYS) {
			totalItemsAgg[key] += item[key] ?? 0
		}
	})

	const totalSpends =
		totalItemsAgg.spendTotal +
		totalItemsAgg.accountFBTotal +
		totalItemsAgg.creoTotal +
		totalItemsAgg.installsTotal +
		totalItemsAgg.agencyPercentTotal +
		totalItemsAgg.slicesTotal
	// re-calculate ROI by formula
	if (totalSpends) {
		totalItemsAgg.roi = 100 * totalItemsAgg.profit / (totalSpends)
	}

	return totalItemsAgg
}

export const getRevenueSpendAgencyList = (
	revenueAgencies: IRevenueAgencyDropDown[] | null,
	items: TRevenueSpendByAccount[] | TRevenueSpendByTeam[],
	showEmptySpendAgencyColumns: boolean
) => {
	const agencyList = revenueAgencies?.filter(
		(item) => {
			const isfakeTypeNone = item.fakeType === ERevenueAgencyFakeType.None

			if (!isfakeTypeNone) {
				return false
			}

			if (showEmptySpendAgencyColumns) {
				return true
			}

			const agencyId = item?.id

			const spendDataByAgency = items?.map(it => {
				return it?.spend?.filter(_ => _?.agencyId === agencyId)
			})?.flat()

			const spendAmountByAgency = spendDataByAgency?.reduce(
				(sum, item) => sum + (item?.amount ?? 0), 0
			)

			return !!spendAmountByAgency
		}
	) ?? []

	return agencyList
}

export const RevenueSpendByAccountListTable = (props: IRevenueSpendByAccountListTableProps) => {
	const {
		items = [],
		skip = 0,
		sortBy,
		setSortBy,
		order,
		setOrder,
		onViewRevenueCpaDetails,
		onViewRevenueSpendDetails,
		onViewOfferSpendDetails,
		onViewRevenueDetails,
		onViewSpendDetails,
		showEmptySpendAgencyColumns = false
	} = props
	const { t } = useTranslation()
	const revenueAgencies = useAtomValue(state.revenueAgenciesAtom)
	const profile = useAtomValue(state.profileAtom)
	const $access = new Access(profile ?? null)

	const agencyList = getRevenueSpendAgencyList(revenueAgencies, items, showEmptySpendAgencyColumns)
	//console.log('RevenueSpendByAccountListTable: ', { revenueAgencies, agencyList, items })

	const totalItemsAgg = getRevenueSpendTotalItemsAgg(items)

	const revenueSpendListTableConfig: IUiTableConfig<TRevenueSpendByAccount>[] = [
		{
			key: 'at',
			title: t(ALL_LANG_KEY.revenue_spend_by_account_list_table.column_date),
			classesToAdd: {
				th: 'text-center border-end',
				td: 'text-center border-end'
			},
			rowCellFormatter: ({ rowData }) => {
				if (!rowData) {
					return ''
				}

				const at = rowData?.at ?? null

				return <>
					{dt.utc(at).isValid() ? dt.utc(at).format('L') : '-'}
				</>
			}
		},
		{
			key: 'accountId',
			title: t(ALL_LANG_KEY.revenue_spend_by_account_list_table.column_account),
			classesToAdd: {
				th: 'text-center',
				td: 'text-center'
			},
			rowCellFormatter: ({ rowData }) => {
				if (!rowData) {
					return ''
				}

				const accountId = rowData?.id ?? null
				const auth = rowData?.auth ?? null
				const login = auth?.login ?? null
				return <>
					<strong>[id: {accountId}]</strong>
					&nbsp;
					{login ?? '?'}
				</>
			}
		},
		{
			key: 'account.extra.keitaroLogin',
			title: t(ALL_LANG_KEY.revenue_spend_by_account_list_table.column_kt_group),
			classesToAdd: {
				th: 'text-center border-end',
				td: 'text-center border-end'
			},
			rowCellFormatter: ({ rowData }) => {
				if (!rowData) {
					return <UiTotalLabel/>
				}

				const extra = rowData?.extra ?? null
				const valueFormat: string = extra?.keitaroLogin ?? ''
				return valueFormat
			}
		},
		{
			key: 'revenueCpaTotal',
			title: t(ALL_LANG_KEY.revenue_spend_by_account_list_table.column_revenue_cpa),
			classesToAdd: {
				th: 'text-center',
				td: 'text-center'
			},
			rowCellFormatter: ({ value, rowData, rowsData, rowIndex, cellIndex }) => {
				const className = 'text-success'

				if (!rowData) {
					const total = totalItemsAgg.revenueCpaTotal
					return renderTotal(total, rowIndex, cellIndex, className)
				}
				const total = rowData.revenueCpaTotal ?? 0
				return renderByRevenue($access.$(ERoleAccess.RevenueSpendByAccountList), rowData, onViewRevenueCpaDetails)(total, rowIndex, cellIndex, className)
			}
		},
		/*{
			key: 'revenueSpendTotal',
			title: 'Revenue Spend',
			classesToAdd: {
				th: 'text-center',
				td: 'text-center'
			},
			rowCellFormatter: ({ value, rowData, rowsData, rowIndex, cellIndex }) => {
				const className = 'text-success'

				if (!rowData) {
					const total = totalItemsAgg.revenueSpendTotal
					return renderTotal(total, rowIndex, cellIndex, className)
				}
				const total = rowData.revenueSpendTotal ?? 0
				return renderByRevenue($access.$(ERoleAccess.RevenueSpendByAccountList), rowData, onViewRevenueSpendDetails)(total, rowIndex, cellIndex, className)
			}
		},*/
		{
			key: 'offerSpendTotal',
			title: t(ALL_LANG_KEY.revenue_spend_by_account_list_table.column_revenue_offer_spend),
			classesToAdd: {
				th: 'text-center',
				td: 'text-center'
			},
			rowCellFormatter: ({ value, rowData, rowsData, rowIndex, cellIndex }) => {
				const className = 'text-success'

				if (!rowData) {
					const total = totalItemsAgg.offerSpendTotal
					return renderTotal(total, rowIndex, cellIndex, className)
				}
				const total = rowData.offerSpendTotal ?? 0
				return renderByRevenue($access.$(ERoleAccess.RevenueSpendByAccountList), rowData, onViewOfferSpendDetails)(total, rowIndex, cellIndex, className)
			}
		},
		{
			key: 'revenueTotal',
			title: t(ALL_LANG_KEY.revenue_spend_by_account_list_table.column_revenue_total),
			classesToAdd: {
				th: 'text-center',
				td: 'text-center'
			},
			rowCellFormatter: ({ value, rowData, rowsData, rowIndex, cellIndex }) => {
				const className = 'text-success'

				if (!rowData) {
					const total = totalItemsAgg.revenueCpaTotal + totalItemsAgg.offerSpendTotal
					return renderTotal(total, rowIndex, cellIndex, className)
				}
				const total = (rowData.revenueCpaTotal ?? 0) + (rowData.offerSpendTotal ?? 0)
				return renderTotal(total, rowIndex, cellIndex, className)
			}
		},
		{
			key: 'spendTotal',
			title: t(ALL_LANG_KEY.revenue_spend_by_account_list_table.column_spend),
			classesToAdd: {
				th: 'text-center',
				td: 'text-center'
			},
			rowCellFormatter: ({ value, rowData, rowsData, rowIndex, cellIndex }) => {
				const className = 'text-danger'
				const filterFunc = (item: TRevenueSpendAgg) => {
					return item.agency?.fakeType === ERevenueAgencyFakeType.None
				}

				if (!rowData) {
					const total = totalItemsAgg.spendTotal
					return renderTotal(total, rowIndex, cellIndex, className)
				}

				const total = rowData.spendTotal ?? 0
				const rowDataFiltered: TRevenueSpendByAccount = {
					...rowData,
					spend: rowData.spend.filter(filterFunc)
				}
				//console.log('spendTotal --- rowCellFormatter --- spendTotal ---', { rowData, rowDataFiltered })
				return renderBySpend($access.$(ERoleAccess.RevenueSpendByAccountList), rowDataFiltered, onViewSpendDetails)(total, rowIndex, cellIndex, className)
			}
		},
		{
			key: 'roi',
			title: t(ALL_LANG_KEY.revenue_spend_by_account_list_table.column_roi),
			classesToAdd: {
				th: 'text-center',
				td: 'text-center'
			},
			rowCellFormatter: ({ value, rowData, rowsData, rowIndex, cellIndex }) => {
				if (!rowData) {
					const total = totalItemsAgg.roi
					const className = total >= 0 ? 'text-success' : 'text-danger'
					return renderTotal(total, rowIndex, cellIndex, className)
				}
				const total = rowData.roi ?? 0
				const className = total >= 0 ? 'text-success' : 'text-danger'
				return renderTotal(total, rowIndex, cellIndex, className)
			}
		},
		{
			key: 'accountFBTotal',
			title: t(ALL_LANG_KEY.revenue_spend_by_account_list_table.column_account_fb),
			classesToAdd: {
				th: 'text-center',
				td: 'text-center'
			},
			rowCellFormatter: ({ value, rowData, rowsData, rowIndex, cellIndex }) => {
				const filterFunc = (item: TRevenueSpendAgg) => {
					return item.agency?.fakeType === ERevenueAgencyFakeType.AccountFB
				}

				if (!rowData) {
					const total = totalItemsAgg.accountFBTotal

					if (!total) {
						return NO_DATA_CELL
					}

					return renderTotal(total, rowIndex, cellIndex)
				}

				const total = rowData.accountFBTotal ?? 0
				if (!total) {
					return NO_DATA_CELL
				}

				const rowDataFiltered: TRevenueSpendByAccount = {
					...rowData,
					spend: rowData.spend.filter(filterFunc)
				}
				//console.log('accountFBTotal --- rowCellFormatter --- spendTotal ---', { rowData, rowDataFiltered })
				return renderBySpend($access.$(ERoleAccess.RevenueSpendByAccountList), rowDataFiltered, onViewSpendDetails)(total, rowIndex, cellIndex)
			}
		},
		{
			key: 'creoTotal',
			title: t(ALL_LANG_KEY.revenue_spend_by_account_list_table.column_creo),
			classesToAdd: {
				th: 'text-center',
				td: 'text-center'
			},
			rowCellFormatter: ({ value, rowData, rowsData, rowIndex, cellIndex }) => {
				const filterFunc = (item: TRevenueSpendAgg) => {
					return item.agency?.fakeType === ERevenueAgencyFakeType.Creo
				}

				if (!rowData) {
					const total = totalItemsAgg.creoTotal

					if (!total) {
						return NO_DATA_CELL
					}

					return renderTotal(total, rowIndex, cellIndex)
				}

				const total = rowData.creoTotal ?? 0
				if (!total) {
					return NO_DATA_CELL
				}

				const rowDataFiltered: TRevenueSpendByAccount = {
					...rowData,
					spend: rowData.spend.filter(filterFunc)
				}
				//console.log('creoTotal --- rowCellFormatter --- spendTotal ---', { rowData, rowDataFiltered })
				return renderBySpend($access.$(ERoleAccess.RevenueSpendByAccountList), rowDataFiltered, onViewSpendDetails)(total, rowIndex, cellIndex)
			}
		},
		{
			key: 'installsTotal',
			title: t(ALL_LANG_KEY.revenue_spend_by_account_list_table.column_installs),
			classesToAdd: {
				th: 'text-center',
				td: 'text-center'
			},
			rowCellFormatter: ({ value, rowData, rowsData, rowIndex, cellIndex }) => {
				const filterFunc = (item: TRevenueSpendAgg) => {
					return item.agency?.fakeType === ERevenueAgencyFakeType.Installs
				}

				if (!rowData) {
					const total = totalItemsAgg.installsTotal

					if (!total) {
						return NO_DATA_CELL
					}

					return renderTotal(total, rowIndex, cellIndex)
				}

				const total = rowData.installsTotal ?? 0
				if (!total) {
					return NO_DATA_CELL
				}

				const rowDataFiltered: TRevenueSpendByAccount = {
					...rowData,
					spend: rowData.spend.filter(filterFunc)
				}
				//console.log('installsTotal --- rowCellFormatter --- spendTotal ---', { rowData, rowDataFiltered })
				return renderBySpend($access.$(ERoleAccess.RevenueSpendByAccountList), rowDataFiltered, onViewSpendDetails)(total, rowIndex, cellIndex)
			}
		},
		{
			key: 'slicesTotal',
			title: t(ALL_LANG_KEY.revenue_spend_by_account_list_table.column_slices),
			classesToAdd: {
				th: 'text-center',
				td: 'text-center'
			},
			rowCellFormatter: ({ value, rowData, rowsData, rowIndex, cellIndex }) => {
				const filterFunc = (item: TRevenueSpendAgg) => {
					return item.agency?.fakeType === ERevenueAgencyFakeType.Slices
				}

				if (!rowData) {
					const total = totalItemsAgg.slicesTotal

					if (!total) {
						return NO_DATA_CELL
					}

					return renderTotal(total, rowIndex, cellIndex)
				}

				const total = rowData.slicesTotal ?? 0
				if (!total) {
					return NO_DATA_CELL
				}

				const rowDataFiltered: TRevenueSpendByAccount = {
					...rowData,
					spend: rowData.spend.filter(filterFunc)
				}
				//console.log('slicesTotal --- rowCellFormatter --- spendTotal ---', { rowData, rowDataFiltered })
				return renderBySpend($access.$(ERoleAccess.RevenueSpendByAccountList), rowDataFiltered, onViewSpendDetails)(total, rowIndex, cellIndex)
			}
		},
		{
			key: 'agencyPercentTotal',
			title: t(ALL_LANG_KEY.revenue_spend_by_account_list_table.column_agency_percent),
			classesToAdd: {
				th: 'text-center border-end',
				td: 'text-center border-end'
			},
			rowCellFormatter: ({ value, rowData, rowsData, rowIndex, cellIndex }) => {
				const filterFunc = (item: TRevenueSpendAgg) => {
					return item.agency?.fakeType === ERevenueAgencyFakeType.None
				}

				if (!rowData) {
					const total = totalItemsAgg.agencyPercentTotal
					return renderTotal(total, rowIndex, cellIndex)
				}

				const total = rowData.agencyPercentTotal ?? 0
				const rowDataFiltered: TRevenueSpendByAccount = {
					...rowData,
					spend: rowData.spend.filter(filterFunc)
				}
				//console.log('agencyPercentTotal --- rowCellFormatter --- spendTotal ---', { rowData, rowDataFiltered })
				return renderBySpend($access.$(ERoleAccess.RevenueSpendByAccountList), rowDataFiltered, onViewSpendDetails)(total, rowIndex, cellIndex)
			}
		},
		{
			key: 'profit',
			title: t(ALL_LANG_KEY.revenue_spend_by_account_list_table.column_profit),
			classesToAdd: {
				th: 'text-center border-end',
				td: 'text-center border-end'
			},
			rowCellFormatter: ({ value, rowData, rowsData, rowIndex, cellIndex }) => {
				if (!rowData) {
					const total = totalItemsAgg.profit
					const className = total >= 0 ? 'text-success' : 'text-danger'
					return renderTotal(total, rowIndex, cellIndex, className)
				}
				const total = rowData.profit ?? 0
				const className = total >= 0 ? 'text-success' : 'text-danger'
				return renderTotal(total, rowIndex, cellIndex, className)
			}
		},

		...(agencyList.map((agencyItem, agencyItemIndex): IUiTableConfig<TRevenueSpendByAccount> => {
			const agencyId = agencyItem?.id
			const agencyName = agencyItem?.name

			return {
				key: `agency-spend-${agencyName}-${agencyId ?? agencyItemIndex}`,
				title: `${agencyName}`,
				classesToAdd: {
					th: 'text-center',
					td: 'text-center'
				},
				rowCellFormatter: ({ value, rowData, rowsData, rowIndex, cellIndex }) => {
					const filterFunc = (item: TRevenueSpendAgg) => {
						return item.agencyId === agencyId
					}

					if (!rowData) {
						let total = 0
						items.forEach(rowDataItem => {
							const spendAmountByAgency = rowDataItem?.spend?.filter(filterFunc)
								.reduce((sum, item) => sum + (item?.amount ?? 0), 0)

							total += spendAmountByAgency
						})

						if (!total) {
							return NO_DATA_CELL
						}
						//console.log('agencySpendTotal --- TOTAL --- rowCellFormatter --- spendTotal ---', { agencyItem, rowData })
						return renderTotal(total, rowIndex, cellIndex)
					}

					const spendAmountByAgency = rowData?.spend?.filter(filterFunc)
						.reduce((sum, item) => sum + (item?.amount ?? 0), 0)
					if (!spendAmountByAgency) {
						return NO_DATA_CELL
					}

					const rowDataFiltered: TRevenueSpendByAccount = {
						...rowData,
						spend: rowData.spend.filter(filterFunc)
					}
					//console.log('agencySpendTotal --- rowCellFormatter --- spendTotal ---', { agencyItem, rowData, rowDataFiltered })
					return renderBySpend($access.$(ERoleAccess.RevenueSpendByAccountList), rowDataFiltered, onViewSpendDetails)(spendAmountByAgency, rowIndex, cellIndex)
				}
			}
		}))
	]

	const revenueSpendListTableConfigSortable: IUiTableConfig<TRevenueSpendByAccount>[] = revenueSpendListTableConfig.map(
		(configItem) => {
			const configItemIsSortable = REVENUE_SPEND_BY_ACCOUNT_PAGE_SORT_FIELDS.includes(configItem.key as IRevenueSpendByAccountSortField)

			if (!configItemIsSortable) {
				return configItem
			} else {
				return {
					...configItem,
					isSortable: true,
					headCellFormatter: ({
						value,
						configItem,
						cellIndex
					}) => {

						return headCellFormatterSortable({
							value,
							configItem,
							cellIndex,
							sortBy,
							setSortBy,
							order,
							setOrder
						})
					}
				}
			}
		}
	)

	//console.log('RevenueSpendByAccountListTable:: ', { revenueSpendListTableConfig, revenueSpendListTableConfigSortable })
	const { dataItems, tableClassName } = helpers.getTableItemsWithTotalRows(items)

	return <>
		<UiTable
			data={dataItems}
			config={revenueSpendListTableConfigSortable}
			// TODO: uncomment if necessary
			classesToAdd={{
				// wrapper: 'table-sticky-wrapper',
				table: tableClassName
			}}
		/>
	</>
}